import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Database, Eye, FileJson, Lock, Plus, Search, Table, Trash2 } from "lucide-react";

export default function AdminDatabasePage({
  collections,
  selectedCollection,
  setSelectedCollection,
  fetchDocuments,
  totalDocs,
  viewMode,
  setViewMode,
  createMode,
  setCreateMode,
  newDocData,
  setNewDocData,
  handleCreateDoc,
  searchQuery,
  setSearchQuery,
  loading,
  documents,
  getDocKeys,
  getFilteredDocs,
  formatValue,
  handleResetPassword,
  handleViewDoc,
  handleDeleteDoc,
}) {
  return (
    <div className="grid grid-cols-12 gap-6">
      <div className="col-span-12 lg:col-span-3">
        <Card className="shadow-lg">
          <CardHeader className="pb-3 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <Database className="w-5 h-5 text-indigo-600" />
              Collections
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {collections.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No collections found</p>
            ) : (
              <div className="space-y-2">
                {collections.map((coll) => (
                  <button
                    key={coll.name}
                    onClick={() => fetchDocuments(coll.name)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all flex items-center justify-between ${selectedCollection === coll.name
                      ? "bg-indigo-100 text-indigo-700 border-2 border-indigo-300"
                      : "bg-slate-50 hover:bg-slate-100 border-2 border-transparent"}`}
                  >
                    <span className="font-medium truncate">{coll.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${selectedCollection === coll.name ? "bg-indigo-200" : "bg-slate-200"}`}>{coll.count}</span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="col-span-12 lg:col-span-9">
        {!selectedCollection ? (
          <Card className="shadow-lg">
            <CardContent className="py-20 text-center">
              <Database className="w-20 h-20 mx-auto text-slate-300 mb-4" />
              <p className="text-xl text-slate-600">Select a collection to view documents</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-lg">
            <CardHeader className="border-b bg-slate-50">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-xl">{selectedCollection}</CardTitle>
                  <p className="text-sm text-slate-600">{totalDocs} documents</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSelectedCollection(null)}>Back</Button>
                  <div className="flex border rounded-lg overflow-hidden">
                    <button onClick={() => setViewMode("table")} className={`px-3 py-1.5 text-sm ${viewMode === "table" ? "bg-indigo-600 text-white" : "bg-white"}`}><Table className="w-4 h-4" /></button>
                    <button onClick={() => setViewMode("json")} className={`px-3 py-1.5 text-sm ${viewMode === "json" ? "bg-indigo-600 text-white" : "bg-white"}`}><FileJson className="w-4 h-4" /></button>
                  </div>
                  <Dialog open={createMode} onOpenChange={setCreateMode}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700"><Plus className="w-4 h-4 mr-1" />New</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader><DialogTitle>Create New Document</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        <Textarea value={newDocData} onChange={(e) => setNewDocData(e.target.value)} rows={15} className="font-mono text-sm" placeholder='{"field": "value"}' />
                        <div className="flex gap-2">
                          <Button onClick={handleCreateDoc} className="bg-green-600 hover:bg-green-700">Create Document</Button>
                          <Button variant="outline" onClick={() => setCreateMode(false)}>Cancel</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              <div className="mt-4 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Search documents..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="py-20 text-center text-slate-600">Loading...</div>
              ) : documents.length === 0 ? (
                <div className="py-20 text-center text-slate-600">No documents in this collection</div>
              ) : viewMode === "table" ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        {getDocKeys().slice(0, 6).map((key) => <th key={key} className="px-4 py-3 text-left font-semibold text-slate-700 border-b">{key}</th>)}
                        <th className="px-4 py-3 text-right font-semibold text-slate-700 border-b">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredDocs().map((doc, idx) => (
                        <tr key={doc.id || idx} className="hover:bg-slate-50 border-b">
                          {getDocKeys().slice(0, 6).map((key) => <td key={key} className="px-4 py-3 max-w-[200px] truncate">{formatValue(doc[key])}</td>)}
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              {selectedCollection === "users" && <Button size="sm" variant="ghost" className="text-amber-600" title="Reset Password" onClick={() => handleResetPassword(doc.id)}><Lock className="w-4 h-4" /></Button>}
                              <Button size="sm" variant="ghost" onClick={() => handleViewDoc(doc)}><Eye className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteDoc(doc.id)}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-4 space-y-2">
                  {getFilteredDocs().map((doc, idx) => (
                    <div key={doc.id || idx} className="bg-slate-50 rounded-lg p-4 border">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs text-indigo-600 font-mono">{doc.id || `doc_${idx}`}</span>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleViewDoc(doc)}><Eye className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteDoc(doc.id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      <pre className="text-xs overflow-x-auto bg-white p-3 rounded border">{JSON.stringify(doc, null, 2)}</pre>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
